// === WebSocket Event Bus ===
// 轻量发布/订阅，用于解耦 WS 消息处理与 UI 模块之间的循环依赖。
// 所有 WS 消息先经 emitWsEvent 分发，订阅者按需响应，无需互相引用。

type WsEventHandler = (payload: unknown) => void;

interface WsListener {
  fn: WsEventHandler;
  once: boolean;
}

const wsEventListeners = new Map<string, Set<WsListener>>();
let settingsPanelUnsubscribers: Array<() => void> = [];

/** 订阅 WS 事件，返回取消订阅函数 */
export function onWsEvent(
  type: string,
  handler: WsEventHandler,
  options: { once?: boolean } = {},
): () => void {
  if (!type || typeof handler !== 'function') return () => {};
  const listener: WsListener = { fn: handler, once: !!options.once };
  if (!wsEventListeners.has(type)) wsEventListeners.set(type, new Set());
  const bucket = wsEventListeners.get(type)!;
  bucket.add(listener);
  return () => {
    bucket.delete(listener);
    if (bucket.size === 0) wsEventListeners.delete(type);
  };
}

/** 派发 WS 事件到所有订阅者 */
export function emitWsEvent(type: string, payload: unknown): void {
  const bucket = wsEventListeners.get(type);
  if (!bucket || bucket.size === 0) return;
  for (const listener of Array.from(bucket)) {
    try {
      listener.fn(payload);
    } catch (error) {
      console.error('[WEBCODING-WS-EVENT]', type, error);
    }
    if (listener.once) bucket.delete(listener);
  }
  if (bucket.size === 0) wsEventListeners.delete(type);
}

/** 清除某类型的所有订阅 */
export function clearWsEvent(type: string): void {
  if (!type) return;
  wsEventListeners.delete(type);
}

/** 清除设置面板注册的所有订阅（面板关闭时调用） */
export function clearSettingsPanelSubscriptions(): void {
  for (const dispose of settingsPanelUnsubscribers) {
    try { dispose(); } catch { /* ignore */ }
  }
  settingsPanelUnsubscribers = [];
}

/** 在设置面板内注册订阅（自动纳入生命周期管理） */
export function registerSettingsPanelHandler(
  type: string,
  handler: WsEventHandler,
  options: { once?: boolean } = {},
): () => void {
  const dispose = onWsEvent(type, handler, options);
  settingsPanelUnsubscribers.push(dispose);
  return dispose;
}

/** Promise 化等待指定事件，超时或条件不满足则 reject */
export function waitForWsEvent(
  type: string,
  options: { timeoutMs?: number; predicate?: (payload: unknown) => boolean } = {},
): Promise<unknown> {
  const timeoutMs = Number.isFinite(options.timeoutMs) ? options.timeoutMs! : 6000;
  const predicate = typeof options.predicate === 'function' ? options.predicate : null;
  return new Promise((resolve, reject) => {
    let settled = false;
    const done = (fn: () => void) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      off();
      fn();
    };
    const off = onWsEvent(type, (payload) => {
      if (predicate && !predicate(payload)) return;
      done(() => resolve(payload));
    });
    const timer = setTimeout(() => {
      done(() => reject(new Error(`等待 ${type} 回包超时`)));
    }, timeoutMs);
  });
}
